import {IButton} from "./button.interface";
import {Component, ContentChild, EventEmitter, HostBinding, HostListener, Output, TemplateRef} from "@angular/core";
import {LogCat, LogManager} from "../../../log";
import {ACTION_SUFFIX, TranslateConfigService, TranslateService} from "../../../translate";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({template: ''})
export abstract class BaseButtonComponent {
	public abstract button: IButton;

	@Output() onClick:EventEmitter<any> = new EventEmitter<any>();

	@HostBinding("class.selected") isSelected = false;
	@HostBinding("class.disabled") isDisabled = false;
	@HostBinding("class.enforceClick") isEnforceClick = false;
	@HostBinding("style.border-color") borderColor;
	@HostBinding("style.border-width") borderWidth;
	@HostBinding("style.background-color") backgroundColor;

	@HostBinding("title") tooltip = "";
	@HostBinding("tabindex") tabindex = -1;

	@ContentChild('before', {static:false,read: TemplateRef}) beforeTemplate: TemplateRef<any>;
	@ContentChild('after', {static:false,read: TemplateRef}) afterTemplate: TemplateRef<any>;

	constructor(private translateService: TranslateService,
				private translateConfigService: TranslateConfigService) {
		LogManager.debug(LogCat.UI.Button, "constructor()");
	}

	/**
	 * Button Click handler
	 */
	@HostListener('click', ['$event']) buttonClick(event) {
		LogManager.debug(LogCat.UI.Button, "buttonClick(event)");
		if (this.button.stopPropogation){
			event.stopPropagation();
		}
		if ((this.button.enforceClick || !this.button.isSelected) && !this.button.isDisabled) {
			this.onClick.emit(this.button);
		}
	}

	/**
	 * Key Press handler
	 */
	@HostListener('keypress', ['$event']) keyPress(event) {
		LogManager.debug(LogCat.UI.Button, "keyPressed(event)");
		if (event.keyCode === 13) {
			if ((this.button.enforceClick || !this.button.isSelected) && !this.button.isDisabled) {
				this.onClick.emit(this.button);
			}
		}
	}

	public baseOnInit() {
		LogManager.debug(LogCat.UI.Button, "ngOnInit()");
		this.isSelected = this.button.isSelected;
		this.isDisabled = this.button.isDisabled;
		this.isEnforceClick = this.button.enforceClick;
		this.borderColor = this.button.themeColor ? this.button.themeColor.light : undefined;
		this.borderWidth = "1px";

		if (!this.button.isDisabled && !this.button.isSelected) {
			this.tabindex = 0;
		}


	}

	public baseOnChanges(changes) {
		LogManager.debug(LogCat.UI.Button, "ngOnChanges()");
		if (changes.button) {
			this.isSelected = changes.button.currentValue.isSelected;
			this.isDisabled = changes.button.currentValue.isDisabled;
		}

		if (changes.isSelected) {
			this.backgroundColor = changes.isSelected.currentValue && this.button.themeColor ? this.button.themeColor.dark : undefined;
			this.borderWidth = changes.isSelected.currentValue && this.button.themeColor ? "0" : "1px";
		}

		if (!this.button.isDisabled && !this.button.isSelected) {
			this.tabindex = 0;
		} else {
			this.tabindex = -1;
		}

		if (changes["tooltipPlain"] || changes["tooltipToTranslate"]) {
			if (this.button.tooltipPlain && this.button.tooltipPlain !== '') {
				this.tooltip = this.button.tooltipPlain;
			} else if (this.button.tooltipToTranslate && this.button.tooltipToTranslate.template && this.button.tooltipToTranslate.template !== '') {
				this.translateService
					.get(this.translateConfigService.containsRegisteredTranslateSuffix(this.button.tooltipToTranslate.template) ?
						this.button.tooltipToTranslate.template :
						this.button.tooltipToTranslate.template.concat(ACTION_SUFFIX), {a: this.button.tooltipToTranslate.params})
					.pipe(untilDestroyed(this))
					.subscribe((tooltip: string) => {
						this.tooltip = tooltip;
					});
			}
		}
	}

	public baseOnDestroy() {
		LogManager.debug(LogCat.UI.Button, "ngOnDestroy()");
	}
}
